/**
 * Chart utility functions for Nivo charts
 * Common logic for chart layout, margins, scales, and data analysis
 */

import React from 'react';

/**
 * Tooltip wrapper component that dynamically positions tooltips
 * to prevent them from being cut off at chart edges.
 * 
 * Nivo positions tooltips to the left of the cursor by default.
 * This wrapper detects when the cursor is on the left side of the chart
 * and shifts the tooltip to the right to keep it visible.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Tooltip content
 * @param {number} props.dataIndex - Index of the data point in the series
 * @param {number} props.dataLength - Total number of data points
 * @returns {JSX.Element} Wrapped tooltip with dynamic positioning
 */
export const TooltipWrapper = ({ 
  children, 
  dataIndex, 
  dataLength 
}) => {
  // Determine if we're on the left side based on data position
  // This is more reliable than using pixel coordinates since it doesn't
  // depend on container measurements
  let isLeftSide = false;
  
  if (typeof dataIndex === 'number' && typeof dataLength === 'number' && dataLength > 0) {
    // Use data index ratio - if we're in the first half of the data, we're on the left
    isLeftSide = dataIndex < dataLength / 2;
  }
  
  return (
    <div style={{ 
      // On left side: move tooltip right by 100% (its width) + 20px gap
      // On right side: keep Nivo's default left positioning
      transform: isLeftSide ? 'translateX(calc(100% + 20px))' : 'none',
    }}>
      {children}
    </div>
  );
};

/**
 * Format large numbers for axis labels with K/M/B abbreviations
 * Keeps numbers readable while saving space on axes
 *
 * @param {number} value - The numeric value to format
 * @param {number} [decimalPlacesForM] - Optional. If 2, format millions (M) with 2 decimals (e.g. "2.25M"). Omitted for default (1 decimal). Thousands (K) always use 0 decimals.
 * @returns {string} Formatted string (e.g., "80K", "2.5M", "1.2B" or with decimalPlacesForM=2: "2.25M")
 */
export const formatAxisNumber = (value, decimalPlacesForM = null) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  // Billions
  if (absValue >= 1000000000) {
    return `${sign}${(absValue / 1000000000).toFixed(1)}B`;
  }

  // Millions
  if (absValue >= 1000000) {
    const decimals = decimalPlacesForM !== null ? decimalPlacesForM : 1;
    return `${sign}${(absValue / 1000000).toFixed(decimals)}M`;
  }

  // Thousands
  if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toFixed(0)}K`;
  }

  // Small numbers - show with up to 2 decimal places if needed
  if (absValue < 1 && absValue > 0) {
    return `${sign}${absValue.toFixed(2)}`;
  }

  // Regular numbers (0-999)
  return `${sign}${Math.round(absValue)}`;
};

/**
 * Format X-axis values to ensure whole numbers for discrete temporal data
 * Used for periods, weeks, months, years - no decimal ticks
 * 
 * @param {number} value - The X-axis value to format
 * @returns {string} Formatted integer string
 */
export const formatXAxisInteger = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  
  // Return only if value is a whole number, otherwise return empty string
  // This prevents Nivo from showing decimal ticks like 8.5, 9.5, etc.
  return Number.isInteger(value) ? String(Math.round(value)) : '';
};

/**
 * Format Y-axis values based on value type (percentage, currency, etc.)
 * For percentages: converts decimal to percentage (0.8 -> 80%)
 * For regular numbers: abbreviates large values (80000 -> 80K)
 * 
 * @param {number} value - The Y-axis value to format
 * @param {string} columnName - Name of the Y-axis column
 * @param {Object} columnValueTypes - Mapping of column names to value types
 * @returns {string} Formatted value string
 */
export const formatYAxisValue = (value, columnName, columnValueTypes) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  
  // Get value type from metadata
  const valueType = columnValueTypes?.[columnName];
  
  // Handle percentage values (0.8 -> 80%)
  if (valueType === 'percentage') {
    return `${(value * 100).toFixed(0)}%`;
  }

  // Handle dimensionless ratios — show with 2 decimal places
  if (valueType === 'ratio') {
    return value.toFixed(2);
  }

  // Handle hours — show with 1 decimal + unit
  if (valueType === 'hours') {
    return `${value.toFixed(1)}\u00A0t`;
  }

  // Handle currency-per-hour rates — abbreviate large numbers + unit
  if (valueType === 'currency_per_hour') {
    return `${formatAxisNumber(value)}\u00A0kr/t`;
  }

  // For all other types, use standard number abbreviation
  return formatAxisNumber(value);
};

/**
 * Calculate bottom margin based on number of legend items
 * Automatically adjusts space to accommodate multiple rows of legend items
 * Assumes ~4 items can fit per row with itemWidth of 180px
 * 
 * Margins align with design system spacing scale:
 * - 70px ≈ --spacing-36 (72px) for 1 row
 * - 100px ≈ between spacing values for 2 rows
 * - 140px for 3+ rows
 * 
 * @param {number} itemCount - Number of legend items
 * @returns {number} Bottom margin in pixels
 */
export const calculateLegendMargin = (itemCount) => {
  // Estimate rows needed (assuming ~4 items per row at 180px width)
  const estimatedRows = Math.ceil(itemCount / 4);
  
  if (estimatedRows >= 3) return 140;  // 3+ rows of legend items
  if (estimatedRows === 2) return 100; // 2 rows of legend items
  return 70;                           // 1 row of legend items (default)
};

/**
 * Check if data contains negative values
 * Used to determine if a zero-line marker should be shown
 * 
 * @param {Array} nivoData - Data in Nivo format (array of series)
 * @param {Array} keys - Keys to check (for bar charts)
 * @param {string} dataFormat - 'line' or 'bar'
 * @returns {boolean} True if any negative values exist
 */
export const hasNegativeValues = (nivoData, keys = null, dataFormat = 'line') => {
  if (!nivoData || nivoData.length === 0) return false;
  
  if (dataFormat === 'line') {
    // For line/area charts: check y values in series data
    return nivoData.some(series =>
      series.data && series.data.some(point => point.y < 0)
    );
  } else if (dataFormat === 'bar') {
    // For bar charts: check values for each key
    if (!keys || keys.length === 0) return false;
    return nivoData.some(row =>
      keys.some(key => row[key] < 0)
    );
  }
  
  return false;
};

/**
 * Determine the appropriate x-axis scale type based on data
 * For numeric data (periods, weeks, months), ensures whole number ticks only
 * 
 * @param {Object} chartConfig - Chart configuration from backend
 * @param {Array} nivoData - Data in Nivo format
 * @returns {Object} Nivo xScale configuration
 */
export const determineXScale = (chartConfig, nivoData) => {
  if (!nivoData || nivoData.length === 0) {
    return { type: 'point' }; // Safe default
  }
  
  const xAxisType = chartConfig?.x_axis?.type;
  const firstX = nivoData[0]?.data?.[0]?.x;
  
  // Use point scale for categorical or date strings
  // Use linear scale only for pure numeric data
  if (xAxisType === 'categorical' || typeof firstX === 'string') {
    return { type: 'point' };
  } else if (typeof firstX === 'number') {
    // For numeric X-axis, find min/max to avoid empty space
    let minX = Infinity;
    let maxX = -Infinity;
    
    nivoData.forEach(series => {
      if (series.data) {
        series.data.forEach(point => {
          if (typeof point.x === 'number') {
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
          }
        });
      }
    });
    
    // Set min to data minimum (or 'auto'), not 0
    // Use 'nice: true' to round scale to clean integer values for discrete temporal data
    return { 
      type: 'linear',
      min: minX !== Infinity ? minX : 'auto',
      max: maxX !== -Infinity ? maxX : 'auto',
      nice: true  // Rounds scale to nice integer values
    };
  }
  
  return { type: 'point' }; // Safe default
};

/**
 * Create a zero-line marker configuration for charts with negative values
 * Subtle line to indicate zero without dominating the chart
 * 
 * @returns {Array} Nivo markers configuration
 */
export const createZeroLineMarker = () => {
  return [
    {
      axis: 'y',
      value: 0,
      lineStyle: { 
        stroke: '#d1d5db',  // Light gray (--color-border-lighter)
        strokeWidth: 1 
      },
      legend: '',
      legendOrientation: 'horizontal'
    }
  ];
};

/**
 * Get standard legend configuration for Nivo charts
 * Uses vertical layout for many items to prevent overlap
 * Centers single/double items for better visual balance
 * 
 * Styling aligned with design system:
 * - itemsSpacing: 4px (--spacing-2) for vertical, 12px (--spacing-6) for horizontal
 * - symbolSize: 12px (--font-size-sm)
 * - itemHeight: 20-24px
 * - itemTextColor: #333 (close to --color-text-primary)
 * 
 * @param {number} itemCount - Number of legend items
 * @param {string} anchor - Legend position anchor (default: 'bottom')
 * @returns {Array} Nivo legends configuration
 */
/**
 * Get standard legend configuration for charts
 * 
 * @param {number} itemCount - Number of legend items
 * @param {string} anchor - Legend position anchor ('bottom' for line charts, 'top-right' for bar charts)
 * @returns {Array} Nivo legends configuration
 */
export const getStandardLegendConfig = (itemCount, anchor = 'bottom') => {
  const bottomMargin = calculateLegendMargin(itemCount);
  
  // Bar charts use top-right vertical layout
  if (anchor === 'top-right') {
    return [
      {
        anchor: 'top-right',
        direction: 'column',
        justify: false,
        translateX: -10,
        translateY: 0,
        itemsSpacing: 8,
        itemDirection: 'left-to-right',
        itemWidth: 150,
        itemHeight: 20,
        itemOpacity: 0.85,
        symbolSize: 10,
        symbolShape: 'circle',
        itemTextColor: '#333',
        effects: [
          {
            on: 'hover',
            style: {
              itemOpacity: 1,
              itemTextColor: '#000'
            }
          }
        ]
      }
    ];
  }
  
  // Line charts use bottom horizontal row layout - simple, clean config
  return [
    {
      anchor: 'bottom',
      direction: 'row',
      justify: false,  // Center for fewer items
      translateX: 0,
      translateY: bottomMargin -10,
      itemsSpacing: 8,  // Spacing between different legend items
      itemDirection: 'left-to-right',
      itemWidth: 100,  // Very small width to minimize gap between dot and text
      itemHeight: 24,
      itemOpacity: 0.85,
      symbolSize: 10,
      symbolShape: 'circle',
      itemTextColor: '#333',
      effects: [
        {
          on: 'hover',
          style: {
            itemOpacity: 1,
            itemTextColor: '#000'
          }
        }
      ]
    }
  ];
};

/**
 * Get standard axis configuration for Nivo charts
 * 
 * @param {string} position - 'left' or 'bottom'
 * @param {string} label - Axis label
 * @param {Function} formatFn - Optional format function for tick values
 * @returns {Object} Nivo axis configuration
 */
export const getStandardAxisConfig = (position, label = '', formatFn = null) => {
  const baseConfig = {
    tickSize: 5,
    tickPadding: 5,
    tickRotation: 0
  };
  
  if (position === 'bottom') {
    return {
      ...baseConfig,
      legend: label,
      legendPosition: 'middle',
      legendOffset: 40
    };
  } else if (position === 'left') {
    return {
      ...baseConfig,
      legend: label,
      legendPosition: 'middle',
      legendOffset: -50,
      ...(formatFn && { format: formatFn })
    };
  }
  
  return baseConfig;
};
